import {
  Backdrop,
  Box,
  CircularProgress,
  InputAdornment,
  Typography,
} from "@mui/material";
import {
  SectionTitle,
  StyledOneChildContainer,
  StyledParentContainer,
  StyledTwoChildContainer,
} from "../../StyledComponents/Container";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import EditIcon from "@mui/icons-material/Edit";
import Grid from "@mui/material/Grid2";
import ContainedButton from "../../../../shared/Button/CustomContainedButton";
import OutlinedButton from "../../../../shared/Button/customOutlinedButton";
import CancelPopUp from "../../../PopUp/PopUp";
import SubmitPopUp from "../../../PopUp/PopUp";
import { useRef, useState } from "react";
import CustomTextField from "../../../../shared/CustomTextFeild";
import { postMakePayment } from "../../services/CreditLine/CreditLineServices";
import * as constants from "../../../../constants";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/store";
import { notifyUser } from "../../Utils/notifyUser";
import { NumericFormat } from "react-number-format";

const convertDate = (dateString: string): string => {
  const date = new Date(dateString);
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear();

  return `${month}/${day}/${year}`;
};

const CreditLineSubmit = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const userInfo = useSelector((state: RootState) => state.auth);
  const { t } = useTranslation();
  const translate = t as (key: string) => string;
  const isSubmittingRef = useRef<any>(false);
  const formData = location.state;
  const [openCancelPopUp, setOpenCancelPopUp] = useState(false);
  const [openSubmitPopup, setSubmitPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleCancel = () => {
    setOpenCancelPopUp(true);
  };
  const handlePopUpClose = () => {
    setOpenCancelPopUp(false);
  };
  function handleConfirm() {
    navigate("/creditLineSummaryListing");
  }

  const handleSubmit = () => {
    setSubmitPopup(true);
  };
  const handleSubmitPopUpClose = () => {
    setSubmitPopup(false);
  };
  const handleSubmitConfirm = async () => {
    handleSubmitPopUpClose();
    if (isLoading || isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;
    setIsLoading(true);
    const saveVo = {
      creditlineId: formData.creditlineId,
      clientId: formData.clientId,
      participantId: userInfo.defaultParticipantId,
      name: formData.name,
      type: formData.type,
      description: formData.description,
      interestRate: formData.interestRate,
      projectNumber: formData.projectNumber,
      projectDuration: formData.projectDuration,
      paymentDueDate: formData.paymentDueDate,
      status: formData.status,
      availableCredit: formData.availableCredit,
      version: formData.version,
      creditlineTransactionHistory: formData.creditlineTransactionHistory,
      source: formData.source,
      fundId: formData.fundId,
      amountPaid: formData.amountPaid,
      paymentSourceAccountAvailableBalance:
        formData.paymentSourceAccountAvailableBalance,
    };
    try {
      const response = await postMakePayment(saveVo);
      if (response.status === constants.STATUS_CODES.CREATED) {
        setIsLoading(false);
        const message = response.data.message;
        navigate("/creditLineSummaryListing", { state: { message: message } });
      } else {
        notifyUser(
          response.response.data.message,
          "error",
          translate("review&Submit")
        );
      }
    } catch (error) {
      console.error("Error saving MakePayment details:", error);
    } finally {
      setIsLoading(false);
      setSubmitPopup(false);
    }
  };
  function handleBackClick() {
    navigate("/creditLineMakePayment", { state: { submitData: formData } });
  }

  return (
    <StyledParentContainer>
      <StyledOneChildContainer>
        <StyledTwoChildContainer sx={{ border: "none" }}>
          <SectionTitle>
            {" "}
            {translate("review&Submit")} &nbsp;
            <EditIcon sx={{ cursor: "pointer" }} onClick={handleBackClick} />
          </SectionTitle>
          <StyledTwoChildContainer>
            <Box sx={{ padding: "40px" }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                  <CustomTextField
                    labelText={translate("creditLine")}
                    value={formData?.creditlineId}
                    readOnly={true}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                  <CustomTextField
                    labelText={translate("source")}
                    value={formData?.source}
                    readOnly={true}
                  />
                </Grid>
                <Grid
                  size={{ xs: 12, sm: 0, lg: 4 }}
                  sx={{ display: { lg: "block", sm: "none", xs: "block" } }}
                ></Grid>
                <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                  <CustomTextField
                    labelText={translate("availableCreditLine")}
                    value={formData?.availableCredit}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                    readOnly={true}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                  <NumericFormat
                    customInput={CustomTextField}
                    id="payment-amount"
                    thousandSeparator
                    decimalScale={2}
                    fixedDecimalScale
                    labelText={translate("paymentAmount")}
                    value={formData?.amountPaid || 0}
                    allowNegative={false}
                    readOnly
                    prefix="$"
                  />
                </Grid>
                <Grid
                  size={{ xs: 12, sm: 0, lg: 4 }}
                  sx={{ display: { lg: "block", sm: "none", xs: "block" } }}
                ></Grid>
                <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                  <CustomTextField
                    labelText={translate("paymentEffectiveDate")}
                    value={convertDate(
                      formData.creditlineTransactionHistory[0]
                        ?.paymentEffectiveDate
                    )}
                    readOnly={true}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                  <CustomTextField
                    labelText={translate("paymentDate")}
                    value={convertDate(
                      formData.creditlineTransactionHistory[0]?.paymentDate
                    )}
                    readOnly={true}
                  />
                </Grid>
              </Grid>
            </Box>
          </StyledTwoChildContainer>
          <div className="right-fields" style={{ marginTop: "10px" }}>
            <ContainedButton sx={{ mr: 2 }} onClick={handleSubmit}>
              {translate("submit")}
            </ContainedButton>
            <ContainedButton sx={{ mr: 2 }} onClick={handleBackClick}>
              {translate("back")}
            </ContainedButton>
            <OutlinedButton onClick={handleCancel}>
              {translate("cancel")}
            </OutlinedButton>
          </div>
          <SubmitPopUp
            open={openSubmitPopup}
            popupTitle={translate("confirmSubmit")}
            handleClose={handleSubmitPopUpClose}
            saveChanges={() => handleSubmitConfirm()}
            message={translate("doYouWantToSubmitChanges?")}
          />
          <CancelPopUp
            open={openCancelPopUp}
            popupTitle={translate("confirmCancel")}
            handleClose={handlePopUpClose}
            saveChanges={() => handleConfirm()}
            message={translate("doYouWantToCancelChanges?")}
          />

          <Backdrop
            sx={(theme) => ({
              color: "#fff",
              zIndex: theme.zIndex.drawer + 1,
              display: "flex",
              flexDirection: "column",
            })}
            open={isLoading}
          >
            <CircularProgress color="inherit" />
            <Typography color="white">Please wait...</Typography>
          </Backdrop>
        </StyledTwoChildContainer>
      </StyledOneChildContainer>
    </StyledParentContainer>
  );
};

export default CreditLineSubmit;
